import React, { useState } from "react";
import { ClipboardPaste, Sparkles, ShieldX, Loader2, FileText, ChevronDown, ChevronRight } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { SeverityBadge } from "@/components/common/Badges";
import { toast } from "sonner";

const SAMPLE_SNIPPETS = {
    ABAP: `REPORT ZTEST_DEMO.
START-OF-SELECTION.
  SELECT * FROM bseg INTO TABLE @DATA(lt).
  AUTHORITY-CHECK OBJECT 'S_TCODE'
    ID 'TCD' FIELD 'SE38'.
* sy-subrc not evaluated.`,
    SU24: `TCODE,OBJECT,USOBX_C,USOBT_C
SE38,S_PROGRAM,Y,EDIT
SE38,S_DEVELOP,N,
VA01,V_VBAK_VKO,Y,01`,
    OData: `<edmx:Edmx xmlns:edmx="http://schemas.microsoft.com/ado/2007/06/edmx" Version="1.0">
  <edmx:DataServices>
    <Schema Namespace="Z_DEMO_SRV" xmlns="http://schemas.microsoft.com/ado/2008/09/edm">
      <EntityType Name="Customer"><Property Name="Id" Type="Edm.String"/></EntityType>
      <EntitySet Name="Customers" EntityType="Z_DEMO_SRV.Customer"/>
    </Schema>
  </edmx:DataServices>
</edmx:Edmx>`,
    SU53: `Authorization check failed
Date/Time  06.02.2026 / 10:23:45
User       JDOE
Client     100
Transaction FB60
Program    SAPMF05A

Authorization Object F_BKPF_BUK - Accounting Document: Authorization for Company Codes
   Field ACTVT       Value: 01
   Field BUKRS       Value: 1000
   Not authorized

Authorization Object F_BKPF_BES - Accounting Document: Account Authorization
   Field ACTVT       Value: 01
   Field KOART       Value: K
   Not authorized

Authorization data for user JDOE
Role Z_FI_GL_ACCOUNTANT
   Object F_BKPF_BUK
      ACTVT  01, 02, 03
      BUKRS  2000
   Object S_TCODE
      TCD    FB60, FB03`,
};

/**
 * PasteTextPanel — privacy-safe alternative to file upload.
 *
 * Props:
 * - workspaceId (string|null): if set, "Ingest into workspace" mode (POST /api/workspaces/:id/paste-text)
 *                              otherwise "Quick analyze (ephemeral)" mode (POST /api/quick-analyze)
 * - onIngested (fn): callback after persisted ingestion
 * - compact (bool): smaller header for embedded use
 */
export default function PasteTextPanel({ workspaceId = null, onIngested, compact = false }) {
    const { user } = useAuth();
    const [name, setName] = useState("pasted_definition.txt");
    const [text, setText] = useState("");
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);
    const [open, setOpen] = useState(!compact);

    const roles = user?.roles || [];
    const isAdmin = roles.includes("Admin");
    const canAuth = isAdmin || roles.includes("Execution_All") || roles.includes("Execution_CodeReview");
    const canSU24 = isAdmin || roles.includes("Execution_All") || roles.includes("Execution_SU24Adjust");
    const canOData = isAdmin || roles.includes("Execution_All") || roles.includes("Execution_ODataService");
    const anyAccess = canAuth || canSU24 || canOData;

    const fillSample = (k) => {
        setText(SAMPLE_SNIPPETS[k]);
        setName(k === "ABAP" ? "snippet.abap" : k === "SU24" ? "snippet_su24.csv" : k === "SU53" ? "su53_trace.txt" : "snippet.edmx");
    };

    const submit = async () => {
        if (!text.trim() || text.trim().length < 10) {
            toast.error("Paste at least 10 characters");
            return;
        }
        setLoading(true);
        setResult(null);
        try {
            if (workspaceId) {
                await api.post(`/workspaces/${workspaceId}/paste-text`, { name, text });
                toast.success("Definition ingested into workspace");
                setText("");
                onIngested?.();
            } else {
                const { data } = await api.post("/quick-analyze", { name, text });
                setResult(data);
                toast.success("Snippet analyzed");
            }
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Analysis failed");
        } finally { setLoading(false); }
    };

    if (!anyAccess) {
        return (
            <div className="panel-elevated p-5 flex items-center gap-3 border-l-2 border-rose-500">
                <ShieldX size={18} className="text-rose-400" />
                <div>
                    <div className="text-sm text-white">Paste analysis disabled for your role</div>
                    <p className="text-xs text-slate-500">Ask a platform owner to grant an execution role.</p>
                </div>
            </div>
        );
    }

    return (
        <section className="panel-elevated relative overflow-hidden">
            <header
                onClick={() => setOpen(!open)}
                className="flex items-center justify-between px-5 pt-4 pb-3 border-b border-white/5 cursor-pointer select-none"
            >
                <div className="flex items-center gap-3">
                    <ClipboardPaste size={14} className="text-cyan-400" />
                    <div>
                        <div className="label-eyebrow">Paste · Privacy-safe Intake</div>
                        <h3 className="font-display text-base text-white tracking-tight">
                            {workspaceId ? "Paste definition into workspace" : "Quick Analyze · ephemeral"}
                        </h3>
                    </div>
                </div>
                <div className="flex items-center gap-3">
                    <div className="hidden sm:flex items-center gap-1.5">
                        {canAuth && <RoleChip label="ABAP" color="var(--neon-cyan)" />}
                        {canSU24 && <RoleChip label="SU24" color="var(--neon-red)" />}
                        {canOData && <RoleChip label="OData" color="var(--metal)" />}
                    </div>
                    {open ? <ChevronDown size={14} className="text-slate-500" /> : <ChevronRight size={14} className="text-slate-500" />}
                </div>
            </header>
            <AnimatePresence initial={false}>
                {open && (
                    <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                    >
                        <div className="p-5 space-y-4">
                            <p className="text-xs text-slate-400 leading-relaxed">
                                Paste your SAP definition (ABAP code · SU24 CSV · OData EDMX) directly here.
                                Use this when uploading the file is restricted by your organization's security
                                and compliance policies. Classification is automatic.
                            </p>

                            <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
                                {canAuth && <SampleButton label="Sample · ABAP" onClick={() => fillSample("ABAP")} testid="paste-sample-abap" />}
                                {canSU24 && <SampleButton label="Sample · SU24 CSV" onClick={() => fillSample("SU24")} testid="paste-sample-su24" />}
                                {canOData && <SampleButton label="Sample · OData EDMX" onClick={() => fillSample("OData")} testid="paste-sample-odata" />}
                                {canAuth && <SampleButton label="Sample · SU53 Trace" onClick={() => fillSample("SU53")} testid="paste-sample-su53" />}
                            </div>

                            <div>
                                <label className="label-eyebrow block mb-2">Name (for the workspace)</label>
                                <input
                                    data-testid="paste-name-input"
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    className="field"
                                />
                            </div>
                            <div>
                                <label className="label-eyebrow block mb-2">Definition · paste here</label>
                                <textarea
                                    data-testid="paste-text-input"
                                    value={text}
                                    onChange={(e) => setText(e.target.value)}
                                    rows={compact ? 8 : 12}
                                    placeholder="Paste your SAP artifact definition…"
                                    className="field font-mono text-[12px]"
                                    spellCheck={false}
                                />
                            </div>

                            <div className="flex items-center gap-3">
                                <button
                                    data-testid="paste-submit"
                                    onClick={submit}
                                    disabled={loading || !text.trim()}
                                    className="btn-primary flex items-center gap-2 disabled:opacity-50"
                                >
                                    {loading ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
                                    {loading
                                        ? "Analyzing…"
                                        : workspaceId
                                            ? "Ingest into Workspace"
                                            : "Quick Analyze"}
                                </button>
                                <span className="text-[10px] font-mono uppercase tracking-wider text-slate-500">
                                    Privacy mode · nothing leaves UTSAVA
                                </span>
                            </div>

                            {result && (
                                <div data-testid="paste-result" className="panel p-4 mt-3">
                                    <div className="flex items-center gap-3 mb-3">
                                        <span className="font-mono text-[11px] uppercase tracking-wider text-cyan-300/80 border border-cyan-500/30 rounded-sm px-2 py-0.5">
                                            {result.classified_as}
                                        </span>
                                        <span className="text-[11px] font-mono text-slate-500">
                                            {Math.round((result.classification_confidence || 0) * 100)}% confidence
                                            {result.engine_used && <> · engine: <span className="text-cyan-300">{result.engine_used}</span></>}
                                        </span>
                                    </div>
                                    {result.trace && <TraceSummary trace={result.trace} />}
                                    {result.findings.length === 0 ? (
                                        <div className="text-xs text-emerald-300 flex items-center gap-1.5">
                                            <FileText size={11} /> No issues detected in this snippet.
                                        </div>
                                    ) : (
                                        <ul className="space-y-2">
                                            {result.findings.map((f) => (
                                                <li key={f.id} className="flex items-start gap-3 text-xs">
                                                    <SeverityBadge severity={f.severity} />
                                                    <div className="min-w-0">
                                                        <div className="text-white">{f.title}</div>
                                                        <div className="text-slate-400 mt-0.5">{f.description}</div>
                                                    </div>
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                </div>
                            )}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </section>
    );
}

function SampleButton({ label, onClick, testid }) {
    return (
        <button type="button" data-testid={testid} onClick={onClick} className="btn-ghost text-[11px]">{label}</button>
    );
}

const GAP_LABEL = {
    object_not_granted: "Object not assigned",
    field_value_mismatch: "Field values too narrow",
    insufficient_authorization: "Insufficient authorization (RC 4)",
    no_profile: "No profile in user master (RC 16)",
    granted_but_denied: "Granted on paper — denied at runtime",
    undetermined: "Undetermined — needs SUIM/PFCG check",
};

/** Runtime SU53 / STAUTHTRACE trace read-out: who was denied what, with the
 * value-level gap against the authorizations the user actually holds. */
function TraceSummary({ trace }) {
    const failedChecks = (trace.checks || []).filter((c) => c.result === "failed");
    return (
        <div data-testid="paste-trace-summary" className="mb-3 border border-cyan-500/20 rounded-[4px] p-3 space-y-2">
            <div className="flex flex-wrap items-center gap-1.5 text-[10px] font-mono">
                <span className="px-1.5 py-0.5 rounded-sm bg-cyan-500/15 text-cyan-300 border border-cyan-500/30">
                    {trace.format === "stauthtrace_table" ? "STAUTHTRACE table" : trace.format === "su53_block" ? "SU53 screen dump" : "unrecognized layout"}
                </span>
                <span className="px-1.5 py-0.5 rounded-sm bg-rose-500/10 text-rose-300 border border-rose-500/25">
                    {trace.failed_count} denied
                </span>
                <span className="px-1.5 py-0.5 rounded-sm bg-emerald-500/10 text-emerald-300 border border-emerald-500/25">
                    {trace.passed_count} passed
                </span>
                {trace.users?.length > 0 && (
                    <span className="px-1.5 py-0.5 rounded-sm bg-white/5 text-slate-300 border border-white/10">
                        user{trace.users.length > 1 ? "s" : ""} {trace.users.join(", ")}
                    </span>
                )}
                {trace.tcodes?.length > 0 && (
                    <span className="px-1.5 py-0.5 rounded-sm bg-white/5 text-slate-300 border border-white/10">
                        {trace.tcodes.join(", ")}
                    </span>
                )}
                <span className={`px-1.5 py-0.5 rounded-sm border ${trace.gap_analysis_available ? "bg-violet-500/10 text-violet-300 border-violet-500/25" : "bg-amber-500/10 text-amber-300 border-amber-500/25"}`}>
                    {trace.gap_analysis_available ? "value-level gap analysis" : "no authorization-data section"}
                </span>
            </div>
            {failedChecks.map((c, i) => (
                <div key={`${c.object}-${i}`} data-testid={`paste-trace-check-${c.object}`} className="text-[11px] border-t border-white/5 pt-2">
                    <div className="flex flex-wrap items-center gap-2">
                        <span className="font-mono text-rose-300">{c.object}</span>
                        <span className="font-mono text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm bg-white/5 text-slate-300 border border-white/10">
                            {GAP_LABEL[c.gap_type] || c.gap_type || "denied"}
                        </span>
                        {c.rc !== null && c.rc !== undefined && (
                            <span className="font-mono text-[9px] text-slate-400">RC {c.rc}</span>
                        )}
                        {c.occurrences > 1 && <span className="font-mono text-[9px] text-slate-400">{c.occurrences}× in trace</span>}
                    </div>
                    <div className="mt-1 flex flex-wrap gap-1.5">
                        {(c.gap || []).map((g) => (
                            <span
                                key={g.field}
                                className={`font-mono text-[9px] px-1.5 py-0.5 rounded-sm border ${g.covered ? "bg-emerald-500/10 text-emerald-300 border-emerald-500/25" : "bg-rose-500/10 text-rose-300 border-rose-500/25"}`}
                                title={g.held?.length ? `user holds ${g.held.join(", ")}` : "no value held for this field"}
                            >
                                {g.field}={g.required}{g.held?.length ? ` · holds ${g.held.join(", ")}` : ""}
                            </span>
                        ))}
                    </div>
                    {c.remediation && <p className="text-slate-400 mt-1">{c.remediation}</p>}
                </div>
            ))}
        </div>
    );
}

function RoleChip({ label, color }) {
    return (
        <span className="font-mono text-[9px] uppercase tracking-wider px-1.5 py-0.5 border rounded-sm"
            style={{ borderColor: `${color}55`, color }}>
            {label}
        </span>
    );
}
